import ClockIcon from '@src/svgs/ClockIcon';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import styled from 'styled-components';

import ContentCell from './ContentCell';
import { getContentItems } from './contentSummary';
import { dropTargetHighlight } from './dragStyles';
import { formatDurationCompact } from './folderAggregates';
import FolderContextMenu from './FolderContextMenu';
import FolderIcon from './FolderIcon';
import { selectedRowIndicator } from './selectionStyles';
import { useStoryDropTarget } from './useStoryDropTarget';

import type { Folder } from '@src/types.ts/story';
import type { FC, MouseEvent } from 'react';

type Props = {
  folder: Folder;
  subfolderCount: number;
  storyCount: number;
  /** Recursive total stories across this folder + descendants. */
  aggregateStoryCount?: number;
  /** Recursive total clips across this folder + descendants. */
  aggregateClipCount?: number;
  /** Recursive total duration in seconds across this folder + descendants. */
  aggregateDurationSeconds?: number;
  /** Most recent story `_publishedAt` (ISO) across folder + descendants. */
  aggregateLastUpdate?: string;
  /** Whether this row is the currently highlighted (single-clicked) folder. */
  isSelected?: boolean;
  /** Called on single-click — sets this folder as highlighted without navigating. */
  onSelect?: (folderId: string) => void;
  onOpen: (folderId: string) => void;
  onCopyLink: (folder: Folder) => void;
  onRename: (folder: Folder) => void;
  onDelete: (folder: Folder) => void;
  onStoriesDropped?: (storyIds: string[], folderId: string | null) => void;
};

const FolderRow: FC<Props> = ({
  folder,
  subfolderCount,
  storyCount,
  aggregateStoryCount,
  aggregateClipCount,
  aggregateDurationSeconds,
  aggregateLastUpdate,
  isSelected,
  onSelect,
  onOpen,
  onCopyLink,
  onRename,
  onDelete,
  onStoriesDropped,
}) => {
  const [contextMenuPos, setContextMenuPos] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const { isDropTarget, dropHandlers } = useStoryDropTarget(
    folder.id,
    onStoriesDropped,
  );

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setContextMenuPos({ x: event.clientX, y: event.clientY });
  };

  const totalChildren = subfolderCount + storyCount;
  const storiesTotal = aggregateStoryCount ?? storyCount;
  const itemsLabel =
    totalChildren === 0
      ? 'Empty'
      : storiesTotal === 0
        ? `${totalChildren} folder${totalChildren === 1 ? '' : 's'}`
        : `${storiesTotal} ${storiesTotal === 1 ? 'story' : 'stories'}`;
  const durationLabel =
    aggregateDurationSeconds && aggregateDurationSeconds > 0
      ? formatDurationCompact(aggregateDurationSeconds)
      : '';
  const contentItems = getContentItems({
    clips: aggregateClipCount ?? 0,
    quotes: 0,
    articles: 0,
    social: 0,
  });
  const dateLabel = aggregateLastUpdate
    ? new Date(aggregateLastUpdate).toLocaleDateString()
    : '';

  return (
    <Row
      $selected={isSelected}
      onClick={event => {
        event.stopPropagation();
        onSelect?.(folder.id);
      }}
      onDoubleClick={() => onOpen(folder.id)}
      onContextMenu={handleContextMenu}
      {...dropHandlers}
      data-drop-target={isDropTarget || undefined}
      data-testid={`folder-row-${folder.id}`}
    >
      <Left>
        <FolderIcon width={18} height={18} style={{ flexShrink: 0 }} />
        <TitleBlock>
          <Name>{folder.name}</Name>
          <Meta>{itemsLabel}</Meta>
        </TitleBlock>
      </Left>
      <Right data-cols="true">
        <DurationCol title="Total duration">
          {durationLabel && (
            <>
              <ClockIcon />
              <span>{durationLabel}</span>
            </>
          )}
        </DurationCol>
        <ContentCell items={contentItems} />
        {/* Empty topics col so columns line up with story rows. */}
        <TopicsCol />
        <DateCol>{dateLabel}</DateCol>
        <FolderContextMenu
          onCopyLink={() => onCopyLink(folder)}
          onRename={() => onRename(folder)}
          onDelete={() => onDelete(folder)}
          contextMenuPos={contextMenuPos}
          onCloseContextMenu={() => setContextMenuPos(null)}
        />
      </Right>
    </Row>
  );
};

export default observer(FolderRow);

const Row = styled.div<{ $selected?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-lg);
  padding: 12px var(--spacing-md);
  border-radius: 0;
  background: transparent;
  cursor: pointer;
  transition:
    background 120ms ease,
    box-shadow 120ms ease;
  ${({ $selected }) => $selected && selectedRowIndicator}

  &:hover {
    background: ${({ $selected }) =>
      $selected ? 'var(--surface-elevated)' : 'var(--surface)'};
  }

  &[data-drop-target] {
    ${dropTargetHighlight}
    border-radius: 8px;
  }
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: var(--text-primary);
  min-width: 0;
  flex: 1;
`;

// Name + child-count subtitle stack vertically (the count moved out of its own
// column to remove the empty gap between story names and the Length column).
const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const Name = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-normal);
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Meta = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  flex-shrink: 0;
`;

const Col = styled.span`
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  color: var(--text-secondary);
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 5px;

  svg {
    color: var(--text-muted);
    flex-shrink: 0;
  }
`;

// Widths must match UnfolderedStoryRow's columns so values line up vertically
// across folder rows and story rows.
const DurationCol = styled(Col)`
  width: 72px;
  justify-content: flex-start;
`;

const TopicsCol = styled(Col)`
  width: 215px;
  justify-content: flex-start;
`;

const DateCol = styled(Col)`
  width: 88px;
  justify-content: flex-end;
`;
